import ColorPicker from '../../framework/frame-pc/components/color-picker';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { ItemSymbolType } from '@editor/src/js/constants/toolbar-data';
import { browser } from '../../slide/infrastructure/module/independent/agent/common';
import { rgbaToHexA } from '../../framework/frame-pc/components/color-picker/utils';
import { getMenuValues } from '@src/slide/application/service/menu-manager/menu-values';

interface ToolbarItem {
    id: string;
    defaultValue?: any;
    value?: any;
    callback?: (menuType: string, id: string, value: any) => void;
    disabled?: boolean;
    selected?: boolean;
    data?: any[];
}
declare global {
    interface Window {
        appServices: any;
    }
}
class AutoTestConfig {
    /**
     * 显示取色器，并传入坐标位置取色
     * @param pointX
     * @param pointY
     */
    public static showAndClick = (pointX: number, pointY: number): Promise<string> => new Promise((resolve) => {
        const dom = document.getElementById('PopupWrapper');
        ColorPicker.show(dom, {
            handleColorChange: (color: string) => resolve(color),
            autoTest: true,
            testPoint: {
                pointX,
                pointY,
            },
        });
    });

    /**
     * rgba to hex
     * @param color
     * @returns {string}
     */
    public static rgbaToHex = (color: string) => rgbaToHexA(color).hex;

    /**
     * 传入 id 和 defaultValue 模拟工具栏按钮点击事件
     * @param id （参见'@editor/src/js/constants/style-panel-data'中的id）
     * @param defaultValue
     * （字体大小[-1,1]和水平居中['horizontal_left','horizontal_center','horizontal_right']需要传入 defaultValue 来区分按钮）
     */
    public static clickOnToolbarItem = (id: string, defaultValue?: any) => {
        const stylePanelData = window.appServices.editableServiceLayer?.menuManager.stylePanelMenu();
        let toolbarItem: any | null = null;
        const { values } = stylePanelData;
        const value = values?.[id];

        stylePanelData.items?.forEach((item) => {
            if (item.id === id) {
                if (!item.defaultValue || (item.defaultValue && item.defaultValue === defaultValue)
                    || id === ActionType.ITEMNUMBER || id === ActionType.DIGITNUMBER) {
                    toolbarItem = item;
                }
            }
        });
        if (toolbarItem?.defaultValue) {
            if (id === ActionType.FONTSIZE) {
                toolbarItem.value = value;
                if (Object.prototype.toString.call(value) === '[object String]') {
                    const fontsize = parseFloat(value.match(/\d+(\.\d+)?/g));
                    const { defaultValue } = toolbarItem;
                    toolbarItem.disabled = !!AutoTestConfig.isInRange(fontsize, defaultValue);
                }
            } else if (id === ActionType.ITEMNUMBER || id === ActionType.DIGITNUMBER) {
                if (toolbarItem.defaultValue === value) {
                    toolbarItem.value = ItemSymbolType.ITEMSYMBOL_NONE;
                }
            } else {
                toolbarItem.value = defaultValue;
            }
        } else {
            toolbarItem.value = value;
        }
        toolbarItem.callback = stylePanelData.callback;

        AutoTestConfig.clickOnItem(toolbarItem, id);
    };

    /**
     * 字号小于等于8时减小字号按钮置灰
     * 字号大于等于192时增加字号按钮置灰
     * @param fontsize
     * @param defaultValue
     * @returns {boolean}
     */
    public static isInRange(fontsize: number, defaultValue: number) {
        return ((fontsize <= 8) && (defaultValue === -4)) || ((fontsize >= 192) && (defaultValue === 4));
    }

    /**
     * 调用 clickOnToolbarItem 设置格式后，传入 id 获取样式
     * @param id
     * @returns {*}
     */
    public static getCurrentValue = (id: string) => {
        const model = window.appServices.viewableServiceLayer?.selectionService?.selectedElements.getCurrentModel();
        const value = getMenuValues(model)[id];
        return value;
    };

    public static clickOnItem = (item: ToolbarItem, id: string) => {
        switch (id) {
            case ActionType.FONTSIZE: {
                let fontsize = parseFloat(item.value.match(/\d+(\.\d+)?/g));
                fontsize += parseInt(item.defaultValue, 10);
                fontsize = (fontsize < 8) ? 8 : (fontsize > 192) ? 192 : fontsize;
                item.callback?.(MenuType.TOOLBAR, id, `font-size-${fontsize}pt`);
                break;
            }
            default: {
                if (item.value !== null) {
                    let { value } = item;
                    if (typeof value === 'boolean') {
                        value = !value;
                    }
                    item.callback?.(MenuType.TOOLBAR, id, value);
                } else {
                    item.callback?.(MenuType.TOOLBAR, id, null);
                }
            }
        }
    };

    public static _getStylePanelData(id: string) {
        const stylePanelData = window.appServices.editableServiceLayer?.menuManager.stylePanelMenu();
        let panelData: any[] = [];
        const { values } = stylePanelData;

        stylePanelData.items?.forEach((item) => {
            if (item.id === id) {
                panelData = item.data;
            }
        });

        const value = values?.[id];
        panelData.forEach((item) => {
            if (item.defaultValue !== null && item.defaultValue !== undefined) {
                item.value = item.defaultValue;
                if (item.defaultValue === value) {
                    item.selected = true;
                } else {
                    item.selected = false;
                }
            }
        });

        return {
            panelData,
            value,
        };
    }

    /**
     * 调起本地文件选择器，填充背景图片
     */
    public static setBgImgWithLocalFile() {
        const input = document.getElementById('select_pic_input');
        input?.click();
        window.appServices.editableServiceLayer.copyPasteManager.imageEditManager.imageReplaceAction = 2; // 代表是替换背景图片
        window.appServices.editableServiceLayer.copyPasteManager.imageEditManager.imageReplacing = true;
        window.appServices.editableServiceLayer.copyPasteManager.imageEditManager.clipDataWaiting = false;
    }

    /**
     * 给元素添加任意效果
     * @param {object} value
     * let value = {
     *  [effectName]: {...specificEffectProps}
     * }
     * NOTE:
     *  1. effectName:          {string}  可以是shadow/glow/reflection/softEdge;
     *  2. specificEffectProps: {obj}     effect对应的属性集合;
     */
    public static setEffectForSelectedElement(value: { [key: string]: any }) {
        window.appServices.editableServiceLayer?.menuManager.getSelectedModel().getEffect?.()
            .updateEffectCollection(value);
    }

    /**
     * 全部页面渲染完成时间
     * @returns {number}
     */
    public static getAllSlideRenderedTime = (): number => {
        const presentation = window.appServices?.getUiDataService().presentation;
        if (presentation) {
            return browser.isMobile ? presentation.mobileVisibleTime : presentation.loadTime;
        }
        return -1;
    };

    /**
     * 获取元素某个效果的所有ui属性值
     * @param {number} index 当前幻灯片内第index个元素
     * @param {string} effectName 效果的名字：e.g. shadow/glow/softEdge/reflection
     */
    public static getSpecificEffectUiPropsOfSelectedElement = (index: number, effectName: string) => {
        if (index === 999) {
            const model = window.appServices.viewableServiceLayer?.selectionService.selectedElements.getCurrentModel();
            const effect = model.getEffect?.().getEffectCollection();
            return effect?.[effectName];
        }
        const element = window.appServices.viewableServiceLayer?.selectionService.currentSlide.children[index];
        const { effect } = element.props.style;
        return effect?.[effectName];
    };

    /**
     * 获取元素某个效果的所有sdk属性值
     * @param {number} index 当前幻灯片内第index个元素
     * @param {string} effectName 效果的名字：e.g. outerShadow/innerShadow/glow/softEdge/reflection
     */
    public static getSpecificEffectSdkPropsOfSelectedElement = (index: number, effectName: string): any => {
        if (index === 999) {
            const model = window.appServices.viewableServiceLayer?.selectionService.selectedElements.getCurrentModel();
            const effect = model.getEffect?.().getEffectCollection();
            return effect?.[effectName];
        }
        const element = window.appServices.viewableServiceLayer?.selectionService.currentSlide.children[index];
        const { effectList } = element.sdkData.props;
        return effectList?.[effectName];
    };
}

export function objApproximatelyEqual(obj1, obj2, precision: number) {
    const type1 = getType(obj1);
    const type2 = getType(obj2);
    if (type1 !== type2) {
        expect(obj1).toBe(obj2);
        return;
    }

    if (isSimpleType(obj1)) {
        if (getType(obj1) === '[object Number]') {
            expect(obj1).toBeCloseTo(obj2, precision);
        } else {
            expect(obj1).toBe(obj2);
        }
        return;
    }

    const key1 = Object.keys(obj1);
    const key2 = Object.keys(obj2);

    if (key1.length !== key2.length) {
        expect(obj1).toEqual(obj2);
        return;
    }

    for (let i = 0, len = key1.length; i < len; i++) {
        objApproximatelyEqual(obj1[key1[i]], obj2[key1[i]], precision);
    }
}

function getType(o): string {
    return Object.prototype.toString.call(o);
}

function isSimpleType(o): boolean {
    const t = getType(o);
    return t === '[object Number]' || t === '[object Boolean]' || t === '[object String]';
}

export default AutoTestConfig;
